// PO1-3325 — Lupa de Inconsistências: monta a hierarquia das bolhas a partir
// das linhas da fila (`/autopilot/importacao/:tipo/fila`) e do catálogo de
// motivos que vem junto. Função pura (testada em __tests__/montarHierarquia).
//
// Níveis:
//   CT-e:   [Embarcador, só na visão todos] → Motivo → Transportadora → Documento
//   Fatura: [Embarcador] → Motivo da fatura → ("Esperando CT-e") "CT-e: <motivo
//           do CT-e que a segura>" → Transportadora → Fatura
// Um documento com N motivos entra em cada motivo; os totais exibidos no topo
// (docsUnicos) contam documentos únicos. No último nível, até TETO_DOCUMENTOS
// documentos (maior valor; mais antigos no modo quantidade) e um nó "+N outros".

export const TETO_DOCUMENTOS = 150;
export const MOTIVO_ESPERANDO_CTE = "CT-e não encontrado no sistema";

export const DONOS = {
  transportadora: { nome: "Transportadora corrige", tom: "warning" },
  embarcador: { nome: "Embarcador envia", tom: "info" },
  espera: { nome: "Resolve sozinho", tom: "success" },
  everlog: { nome: "Everlog (cadastro)", tom: "danger" },
  misto: { nome: "Vários", tom: "neutral" },
};

function numero(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function rotuloMotivo(motivo, catalogo) {
  const item = catalogo && catalogo[motivo];
  return (item && item.rotulo) || motivo;
}

function donoMotivo(motivo, catalogo, donoLinha) {
  if (donoLinha) return donoLinha;
  const item = catalogo && catalogo[motivo];
  return (item && item.dono) || "everlog";
}

export function nomeDocumento(row, tipo) {
  const num = String(row.Numero || "").replace(/^0+/, "");
  if (num) return (tipo === "fatura" ? "Fatura " : "CT-e ") + num;
  return row.NomeArquivo || row.id || "(sem número)";
}

// Deduplica por documento (a fila tem uma linha por arquivo).
// Data do SQL Server ("2026-09-17 00:00:00.000 -03:00") em ISO, para o
// Safari também ler.
function paraTempo(v) {
  if (!v) return null;
  const iso = typeof v === "string"
    ? v.replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}(?:\.\d+)?) ?([+-]\d{2}:\d{2}|Z)?$/, (m, d, h, tz) => d + "T" + h + (tz || ""))
    : v;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
}

// Desde quando o documento está parado: `mtime` do arquivo no SFTP (chegada);
// sem ele (visão todos não varre o SFTP), a emissão. O CreationTime do Work_*
// é recriado a cada reprocessamento (QA 05/10: CT-e de agosto com "0 dias"),
// então só serve de último recurso.
export function chegadaDoDocumento(row) {
  if (!row) return null;
  return paraTempo(row.mtime) || paraTempo(row.Dat_Emissao) || paraTempo(row.CreationTime);
}

// Rótulo honesto da conta acima, para o detalhe do documento.
export function rotuloChegada(row) {
  if (row && paraTempo(row.mtime)) return "Na fila há";
  if (row && paraTempo(row.Dat_Emissao)) return "Emitido há";
  return "Na fila há";
}

// QA 05/10 (regra do Rodrigo): a Lupa conta ARQUIVOS, igual à grade da
// Integração — o mesmo CT-e que chegou em 2 arquivos conta 2. Cada linha da fila
// é um arquivo (id wc:/wf:/np:); aqui só tira linha repetida.
export function chaveArquivo(r) {
  return r.id || r.chaveDocumento;
}

export function dedupPorDocumento(rows) {
  const vistos = new Set();
  const out = [];
  (rows || []).forEach((r) => {
    const k = chaveArquivo(r);
    if (vistos.has(k)) return;
    vistos.add(k);
    out.push(r);
  });
  return out;
}

function novoNo(props) {
  return Object.assign({ docs: 0, valor: 0, valorReal: 0, semValor: 0, children: [], _docs: new Set() }, props);
}

function filho(pai, key, props) {
  let no = pai._mapa && pai._mapa.get(key);
  if (!no) {
    no = novoNo(Object.assign({ key: pai.key + "/" + key }, props));
    no._mapa = new Map();
    if (!pai._mapa) pai._mapa = new Map();
    pai._mapa.set(key, no);
    pai.children.push(no);
  }
  return no;
}

// `valor` é o tamanho da bolha (com piso, ou 1 por documento no modo
// Quantidade); `valorReal` é o R$ exibido, sem piso (QA 05/10).
function acumular(caminho, row, valorDoc) {
  const k = chaveArquivo(row);
  caminho.forEach((no) => {
    if (no._docs.has(k)) return;
    no._docs.add(k);
    no.docs += 1;
    no.valor += valorDoc;
    no.valorReal += numero(row.valor);
    if (!(numero(row.valor) > 0)) no.semValor += 1;
  });
}

function ordenar(nos, medida) {
  return nos.slice().sort((a, b) => (medida === "quantidade" ? b.docs - a.docs : b.valor - a.valor) || String(a.nome).localeCompare(String(b.nome)));
}

// Documento vira folha; acima do teto, os demais viram um "+N outros".
function fecharFolhas(no, tipo, medida, teto) {
  if (no._folhas) {
    const folhas = no._folhas.slice().sort((a, b) => {
      if (medida === "quantidade") {
        const ta = chegadaDoDocumento(a.doc) || 0;
        const tb = chegadaDoDocumento(b.doc) || 0;
        return ta - tb;
      }
      return b.valor - a.valor;
    });
    const visiveis = folhas.slice(0, teto);
    const resto = folhas.slice(teto);
    // Mesmo documento em 2 arquivos: a 2ª bolha diz "(2º arquivo)".
    const vezes = new Map();
    const nomeDaFolha = (doc) => {
      const nome = nomeDocumento(doc, tipo);
      const n = (vezes.get(nome) || 0) + 1;
      vezes.set(nome, n);
      return n > 1 ? `${nome} (${n}º arquivo)` : nome;
    };
    no.children = visiveis.map((f) => ({
      key: no.key + "/doc:" + chaveArquivo(f.doc),
      tipo: "documento",
      nome: nomeDaFolha(f.doc),
      dono: no.dono,
      docs: 1,
      valor: f.valor,
      valorReal: numero(f.doc.valor),
      valorNaoLido: !!f.doc.valorNaoLido || !(numero(f.doc.valor) > 0),
      valorEstimado: !!f.doc.valorEstimado,
      doc: f.doc,
      recorte: Object.assign({}, no.recorte, { id: f.doc.id, numero: String(f.doc.Numero || "").replace(/^0+/, "") || null }),
      children: [],
    }));
    if (resto.length) {
      no.children.push({
        key: no.key + "/outros",
        tipo: "outros",
        nome: "+" + resto.length + " outros",
        dono: no.dono,
        docs: resto.length,
        valor: resto.reduce((acc, f) => acc + f.valor, 0),
        valorReal: resto.reduce((acc, f) => acc + numero(f.doc.valor), 0),
        recorte: no.recorte,
        children: [],
      });
    }
    delete no._folhas;
    no.valorNaoLido = no.docs > 0 && no.semValor === no.docs;
    return;
  }
  no.valorNaoLido = no.docs > 0 && no.semValor === no.docs;
  no.children = ordenar(no.children, medida);
  no.children.forEach((c) => fecharFolhas(c, tipo, medida, teto));
}

// Nó que ficou sem documento (todos os motivos dele ocultos pelo filtro de
// dono) sai da árvore.
function podarVazios(no) {
  no.children = (no.children || []).filter((c) => c.docs > 0 || c._folhas);
  no.children.forEach(podarVazios);
}

function limpar(no) {
  delete no._mapa;
  delete no._docs;
  (no.children || []).forEach(limpar);
  return no;
}

/**
 * @param {Array} rows linhas da fila (CT-e ou Fatura), já com motivos/valor/chaveDocumento
 * @param {Object} catalogo motivo → { categoria, dono, rotulo?, explicacao }
 * @param {Object} opts { tipo: 'cte'|'fatura', medida: 'valor'|'quantidade', donosOcultos: Set, verTodos: boolean, teto }
 */
export function montarHierarquia(rows, catalogo, opts) {
  const tipo = (opts && opts.tipo) || "cte";
  const medida = (opts && opts.medida) || "valor";
  const ocultos = (opts && opts.donosOcultos) || new Set();
  const verTodos = !!(opts && opts.verTodos);
  const teto = (opts && opts.teto) || TETO_DOCUMENTOS;

  const docs = dedupPorDocumento(rows);
  const maxValor = docs.reduce((m, r) => Math.max(m, numero(r.valor)), 0);
  // Piso só para documento SEM valor (zerado ou não lido), para ele ainda
  // aparecer. Valor baixo entra como é: no QA de 05/10 o piso em todo valor
  // abaixo de 1% do maior inflou a bolha da SAS de R$ 358 mil para R$ 2,4 mi.
  const piso = maxValor > 0 ? maxValor * 0.01 : 1;
  const valorDe = (r) => {
    if (medida === "quantidade") return 1;
    const v = numero(r.valor);
    return v > 0 ? v : piso;
  };

  const raiz = novoNo({ key: "raiz", tipo: "raiz", nome: tipo === "fatura" ? "Faturas travadas" : "CT-e travados", dono: null, recorte: {} });
  raiz._mapa = new Map();

  docs.forEach((row) => {
    const valorDoc = valorDe(row);
    const motivos = Array.isArray(row.motivos) && row.motivos.length ? row.motivos : [{ motivo: "Outros", categoria: "Outros" }];
    let base = raiz;
    const caminhoBase = [raiz];
    if (verTodos) {
      base = filho(raiz, "emb:" + row.idEmbarcador, {
        tipo: "embarcador",
        nome: row.Embarcador || "Embarcador " + row.idEmbarcador,
        idEmbarcador: row.idEmbarcador,
        dono: "misto",
        recorte: { idEmbarcador: row.idEmbarcador },
      });
      caminhoBase.push(base);
    }
    const transp = (pai, dono) =>
      filho(pai, "t:" + (row.idTransportadoraGrupo != null ? row.idTransportadoraGrupo : row.idTransportadora), {
        tipo: "transportadora",
        nome: row.fantasiaGrupo || row.Fantasia || "Sem nome",
        idTransportadora: row.idTransportadoraGrupo != null ? row.idTransportadoraGrupo : row.idTransportadora,
        dono,
        recorte: Object.assign({}, pai.recorte, { idTransportadora: row.idTransportadoraGrupo != null ? row.idTransportadoraGrupo : row.idTransportadora }),
      });

    motivos.forEach((m) => {
      const dono = donoMotivo(m.motivo, catalogo, m.dono);
      const esperandoCte = tipo === "fatura" && m.motivo === MOTIVO_ESPERANDO_CTE && Array.isArray(row.ctes) && row.ctes.length;
      if (!esperandoCte && ocultos.has(dono)) return;
      const noMotivo = filho(base, "m:" + m.motivo, {
        tipo: "motivo",
        nome: rotuloMotivo(m.motivo, catalogo),
        motivo: m.motivo,
        categoria: m.categoria,
        dono,
        explicacao: catalogo && catalogo[m.motivo] ? catalogo[m.motivo].explicacao : null,
        recorte: Object.assign({}, base.recorte, { categoria: m.categoria, motivo: m.motivo }),
      });

      if (!esperandoCte) {
        const noTransp = transp(noMotivo, dono);
        if (!noTransp._folhas) noTransp._folhas = [];
        noTransp._folhas.push({ doc: row, valor: valorDoc });
        acumular(caminhoBase.concat([noMotivo, noTransp]), row, valorDoc);
        return;
      }

      // Fatura esperando CT-e: um sub-nível por motivo do CT-e que a segura.
      const subMotivos = new Map();
      row.ctes.forEach((c) => {
        (c.motivos || []).forEach((cm) => {
          if (!subMotivos.has(cm.motivo)) subMotivos.set(cm.motivo, cm);
        });
      });
      let entrou = false;
      subMotivos.forEach((cm) => {
        const donoCte = donoMotivo(cm.motivo, catalogo, cm.dono);
        if (ocultos.has(donoCte)) return;
        entrou = true;
        const noSub = filho(noMotivo, "cte:" + cm.motivo, {
          tipo: "submotivo",
          nome: "CT-e: " + rotuloMotivo(cm.motivo, catalogo),
          motivo: m.motivo,
          motivoCte: cm.motivo,
          categoria: m.categoria,
          dono: donoCte,
          explicacao: catalogo && catalogo[cm.motivo] ? catalogo[cm.motivo].explicacao : null,
          recorte: Object.assign({}, noMotivo.recorte, { motivoCte: cm.motivo }),
        });
        const noTransp = transp(noSub, donoCte);
        if (!noTransp._folhas) noTransp._folhas = [];
        noTransp._folhas.push({ doc: row, valor: valorDoc });
        acumular(caminhoBase.concat([noMotivo, noSub, noTransp]), row, valorDoc);
      });
      if (!entrou && noMotivo.docs === 0) {
        // todos os sub-motivos ocultos: tira o nó vazio
        base.children = base.children.filter((c) => c !== noMotivo);
        base._mapa.delete("m:" + m.motivo);
      }
    });
  });

  podarVazios(raiz);
  fecharFolhas(raiz, tipo, medida, teto);
  limpar(raiz);
  raiz.docsUnicos = raiz.docs;
  return raiz;
}

// Totais por dono sobre documentos únicos (rodapé): um documento com dois
// motivos de donos diferentes conta nos dois, mas o total geral conta uma vez.
export function totaisPorDono(rows, catalogo, tipo) {
  const docs = dedupPorDocumento(rows);
  const porDono = {};
  let total = 0;
  let valorTotal = 0;
  docs.forEach((row) => {
    total += 1;
    valorTotal += numero(row.valor);
    const donos = new Set();
    (row.motivos || []).forEach((m) => {
      if (tipo === "fatura" && m.motivo === MOTIVO_ESPERANDO_CTE && Array.isArray(row.ctes) && row.ctes.length) {
        row.ctes.forEach((c) => (c.motivos || []).forEach((cm) => donos.add(donoMotivo(cm.motivo, catalogo, cm.dono))));
      } else {
        donos.add(donoMotivo(m.motivo, catalogo, m.dono));
      }
    });
    donos.forEach((d) => {
      if (!porDono[d]) porDono[d] = { docs: 0, valor: 0 };
      porDono[d].docs += 1;
      porDono[d].valor += numero(row.valor);
    });
  });
  return { total, valorTotal, porDono };
}
